import _ from 'lodash';
import { BlockEmbed } from 'quill/blots/block';
import type { ReactNode } from 'react';
import React, { memo, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ScrollBlot } from '../types';
import Scroll from './Scroll';
import { useUIElementResize } from '../UIElementResizeContext';

export type StoreUpdater<State> = Partial<State> | ((state: State) => State);
type StoreConnectProps<State> = {
  children: (state: State) => ReactNode;
};
export type Store<State> = {
  setState: (newStateOrUpdater: StoreUpdater<State>) => void;
  getState: () => State;
  Connect: (
    props: StoreConnectProps<State>,
  ) => ReturnType<StoreConnectProps<State>['children']>;
};

export const createStore = <T,>(initialState: T): Store<T> => {
  let state: T = initialState;
  let listener: ((newState: T) => void) | null = null;

  const setState = (newStateOrUpdater: StoreUpdater<T>) => {
    state =
      typeof newStateOrUpdater === 'function'
        ? newStateOrUpdater(state)
        : { ...state, ...newStateOrUpdater };

    if (listener) {
      listener(state);
    }
  };

  const Connect = memo(({ children }: StoreConnectProps<T>) => {
    const [innerState, setInnerState] = useState(state);

    const uiElementResize = useUIElementResize();
    useEffect(() => {
      uiElementResize();
    }, [uiElementResize, innerState]);

    // calling quill.setContents and quill.updateContents in e2e tests.
    // does not always trigger a rerender in webkit. useLayoutEffect instead of useEffect
    // fixes this issue by making sure that the listener is set early enough.
    useLayoutEffect(() => {
      listener = setInnerState;
      return () => {
        listener = null;
      };
    }, []);

    return children(innerState);
  });

  return { setState, Connect, getState: () => state };
};

const ATTR_NAME = 'data-blot-value';

class ReactBlockEmbed extends BlockEmbed {
  static create(value: unknown) {
    const node = super.create() as Element;
    node.setAttribute('contenteditable', 'false');
    this.setValue(node, value);
    return node;
  }

  static setValue(domNode: Element, value: unknown) {
    domNode.setAttribute(ATTR_NAME, JSON.stringify(value));
  }

  static value(domNode: Element) {
    const value = domNode.getAttribute(ATTR_NAME);
    try {
      return value ? JSON.parse(value) : null;
    } catch (err) {
      // ignore errors since DOM may be manipulated outside of Quill
    }
    return null;
  }

  id = _.uniqueId();
  store: Store<unknown>;

  constructor(scroll: ScrollBlot, domNode: Node, value: unknown) {
    // @ts-expect-error TODO: fix this next time the file is edited.
    super(scroll, domNode, value);

    this.store = createStore({});

    this.render = this.render.bind(this);
  }

  setValue(value: unknown) {
    this.statics.setValue(this.domNode, value);
  }

  setState(updater: StoreUpdater<unknown>) {
    this.store.setState(updater);
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  render() {
    return null;
  }

  renderPortal() {
    const { Connect } = this.store;
    const key = `editor_${this.statics.blotName}_${this.id}`;

    return createPortal(
      <Connect>{this.render}</Connect>,
      this.domNode,

      // Hint to the reconciler otherwise the whole component will be torn down and re-created.
      key,
    );
  }
}

export default ReactBlockEmbed;
